import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, debounceTime, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { FilterOperators } from 'src/app/common/enums';
import { PropertyFilterOperator } from 'src/app/common/interfaces/property-filter-operator';
import { buildFilterArray } from 'src/app/common/utils';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { Filter } from 'src/app/models/common/filter';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { DisableErrorStateMatcher } from 'src/app/shared/error-state-matchers/disable-error-state-matcher';

@Component({
  selector: 'app-e-absence-conclusions-list-filters',
  templateUrl: './e-absence-conclusions-list-filters.component.html',
  styleUrls: ['./e-absence-conclusions-list-filters.component.scss'],
  standalone: false
})
export class EAbsenceConclusionsListFiltersComponent implements OnInit {
  private readonly timeBetweenInput = 300;

  @Input() formGroup: FormGroup;
  @Input() hasLegalization: boolean;

  companies$: Observable<DictionaryItem[]>;
  employers$: Observable<DictionaryItem[]>;
  absenceTypes$: Observable<DictionaryItem[]>;
  absenceStatuses$: Observable<DictionaryItem[]>;
  workerStatuses$: Observable<DictionaryItem[]> = this.dictionaryService.getWorkerStatuses();
  declarationTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getDeclarationTypes();
  declarationStatuses$: Observable<DictionaryItem[]> = this.dictionaryService.getDeclarationStatuses();

  private readonly unsubscribe$ = new Subject<void>();
  public readonly matcher = new DisableErrorStateMatcher();

  @Output() filterGridData = new EventEmitter<Filter[]>();

  public static readonly operatorsMap: Map<string, PropertyFilterOperator> = new Map<string, PropertyFilterOperator>([
    ['companyId', { property: 'CompanyId', operator: FilterOperators.Equal }],
    ['startDateFrom', { property: 'StartDate', operator: FilterOperators.DateGreaterThanOrEqual }],
    ['startDateTo', { property: 'StartDate', operator: FilterOperators.DateLessThanOrEqual }],
    ['endDateFrom', { property: 'EndDate', operator: FilterOperators.DateGreaterThanOrEqual }],
    ['endDateTo', { property: 'EndDate', operator: FilterOperators.DateLessThanOrEqual }],
    ['lastName', { property: 'LastName', operator: FilterOperators.Contains }],
    ['firstName', { property: 'FirstName', operator: FilterOperators.Contains }],
    ['statusId', { property: 'StatusId', operator: FilterOperators.Equal }],
    ['absenceTypeId', { property: 'AbsenceTypeId', operator: FilterOperators.Equal }]
  ]);

  constructor(
    private dictionaryService: DictionaryService
  ) { }

  get employer(): FormControl {
    return this.formGroup.get('employer') as FormControl;
  }
  get company(): FormControl {
    return this.formGroup.get('company') as FormControl;
  }

  ngOnInit(): void {
    this.onCompanyChange();

    this.formGroup.valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(1000)).subscribe(() => {

      console.log(this.formGroup);
      if (this.formGroup.invalid) return;
      this.filterGridData.emit(buildFilterArray(this.formGroup, EAbsenceConclusionsListFiltersComponent.operatorsMap));
    });

    this.absenceTypes$ = this.dictionaryService.getAbsenceTypes();
    this.absenceStatuses$ = this.dictionaryService.getAbsenceStatuses()
      .pipe(map(absenceStatuses => absenceStatuses.filter(absenceStatus => absenceStatus.Id !== AbsenceStatus.Draft)));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  resetFilters(): void {
    this.formGroup.reset();
  }

  displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  private onCompanyChange() {
    this.companies$ = this.company.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      tap((value: any) => value?.Id && this.formGroup.patchValue({ companyId: value.Id })),
      switchMap((value: string) => (value ? this.dictionaryService.getCompanies(value) : of([]))),
    );
  }
}
